import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconAlertCircle } from "@tabler/icons-react";
import type React from "react";
import { useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { SurveyForm } from "@/components/SurveyForm";
import type { SurveyDefinition } from "@/utils/survey";
import {
  SurveyDefinitionSchema,
  type SurveyValue,
} from "@/utils/survey-schema";

interface SurveyErrorProps {
  message: string;
  onReset?: () => void;
  loading?: boolean;
}

const SurveyError: React.FC<SurveyErrorProps> = ({
  message,
  onReset,
  loading,
}) => (
  <Stack>
    <Alert color="red" title="Error" icon={<IconAlertCircle />}>
      {message}
    </Alert>
    {onReset && (
      <Button
        variant="outline"
        color="red"
        onClick={onReset}
        disabled={loading}
      >
        Reset Survey
      </Button>
    )}
  </Stack>
);

export const SurveyPage: React.FC = () => {
  const { engagementId, surveyId } = useParams<{
    engagementId: string;
    surveyId: string;
  }>();
  const [error, setError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);

  // Get the survey file
  const { data: surveyFile, isLoading: isLoadingSurveyFile } = client.useQuery(
    "get",
    "/universal/files/{fileId}",
    {
      params: {
        path: {
          fileId: surveyId,
        },
      },
    },
  );

  // Get the filled survey if it exists
  const {
    data: filledSurvey,
    isLoading: isLoadingFilledSurvey,
    refetch: refetchFilledSurvey,
  } = client.useQuery(
    "get",
    "/owner/engagements/{engagementId}/surveys/{surveyId}/filled",
    {
      params: {
        path: {
          engagementId,
          surveyId,
        },
      },
    },
    {
      enabled: !!engagementId && !!surveyId,
      staleTime: 0,
      gcTime: 0,
    },
  );

  // Submit survey mutation
  const submitSurveyMutation = client.useMutation(
    "post",
    "/owner/engagements/{engagementId}/surveys/{surveyId}/submit",
  );

  // Delete filled survey mutation
  const deleteSurveyMutation = client.useMutation(
    "delete",
    "/owner/engagements/{engagementId}/surveys/{surveyId}/filled",
  );

  const handleReset = async () => {
    modals.openConfirmModal({
      title: "Reset Survey",
      children: (
        <Text>
          Are you sure you want to reset this survey? This will delete all your
          answers.
        </Text>
      ),
      labels: { confirm: "Reset", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteSurveyMutation.mutateAsync({
            params: {
              path: {
                engagementId,
                surveyId,
              },
            },
          });
          await refetchFilledSurvey();
          setFormKey((prev) => prev + 1);
        } catch (err) {
          console.error("Error resetting survey:", err);
          setError("Failed to reset survey. Please try again.");
        } finally {
        }
      },
    });
  };

  const isLoading = isLoadingSurveyFile || isLoadingFilledSurvey;

  if (isLoading) {
    return <FancyLoader />;
  }

  if (!surveyFile || !surveyFile.content) {
    return <SurveyError message="Survey not found or could not be loaded." />;
  }

  // Parse the survey definition
  let surveyDefinition: SurveyDefinition;
  try {
    const parsedContent = JSON.parse(surveyFile.content);
    surveyDefinition = SurveyDefinitionSchema.parse(parsedContent);
  } catch (err) {
    console.error("Error parsing survey definition:", err);
    return (
      <SurveyError
        message="Invalid survey format. The survey data may be corrupted."
        onReset={handleReset}
        loading={deleteSurveyMutation.isPending}
      />
    );
  }

  // Extract values from filled survey if available
  const initialValues = filledSurvey?.values || {};

  const handleSubmit = async (
    values: Record<string, SurveyValue>,
    isDraft: boolean,
  ) => {
    setError(null);

    try {
      // Validate answers
      const validationErrors: string[] = [];

      surveyDefinition.questions.forEach((q) => {
        const value = values[q.id];

        if (q.required) {
          if (q.type === "MULTIPLE_CHOICE_MULTI") {
            if (!Array.isArray(value) || value.length === 0) {
              validationErrors.push(
                `${q.label}: Please select at least one option`,
              );
            }
          } else if (q.type === "YES_NO") {
            if (typeof value !== "boolean") {
              validationErrors.push(`${q.label}: Please select Yes or No`);
            }
          } else if (
            !value ||
            typeof value !== "string" ||
            value.trim() === ""
          ) {
            validationErrors.push(`${q.label}: This field is required`);
          }
        }
      });

      if (validationErrors.length > 0) {
        setError(validationErrors.join("\n"));
        return;
      }

      const answers = Object.entries(values).map(([questionId, value]) => ({
        questionId,
        value,
      }));

      await submitSurveyMutation.mutateAsync({
        params: {
          path: {
            engagementId,
            surveyId,
          },
        },
        body: {
          isDraft: isDraft,
          version: surveyDefinition.version,
          answers,
        },
      });

      if (!isDraft) {
        // Navigate back immediately after successful submission
        window.history.back();
      }
    } catch (err) {
      console.error("Error submitting survey:", err);
      setError("Failed to submit survey. Please try again.");
    } finally {
    }
  };

  return (
    <Stack>
      <Header title={surveyFile.name || "Survey"} />

      {error && (
        <Alert color="red" title="Error" icon={<IconAlertCircle />}>
          {error}
        </Alert>
      )}

      <Text>{formKey}</Text>

      <SurveyForm
        key={formKey}
        survey={surveyDefinition}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        loading={submitSurveyMutation.isPending}
      />

      {filledSurvey && (
        <Group>
          <Button
            variant="outline"
            color="red"
            onClick={handleReset}
            disabled={deleteSurveyMutation.isPending}
          >
            Reset Survey
          </Button>
        </Group>
      )}
    </Stack>
  );
};
